import IntlMessages from 'util/IntlMessages';

const myValidator =
  ({ minLength, maxLength, min, max, maxCount, typeComponent }) =>
  (_, value) => {
    return new Promise((resolve, reject) => {
      if (typeComponent === 'tag') {
        if (value) {
          const tagMaxLengthValidate = maxLength && value.some(tag => tag.length > maxLength);
          const tagMinLengthValidate = minLength && value.some(tag => tag.length < minLength);

          if (tagMinLengthValidate) {
            reject(<IntlMessages id="dynamic.form.minLength.message" values={{ minLength }} />);
          }
          if (tagMaxLengthValidate) {
            reject(<IntlMessages id="dynamic.form.maxLength.message" values={{ maxLength }} />);
          }
          if (maxCount && value.length > maxCount) {
            reject(<IntlMessages id="dynamic.form.maxCount.message" values={{ maxCount }} />);
          }
        }
      } else {
        if (minLength && value && value.length < minLength) {
          reject(<IntlMessages id="dynamic.form.minLength.message" values={{ minLength }} />);
        } else if (maxLength && value && value.length > maxLength) {
          reject(<IntlMessages id="dynamic.form.maxLength.message" values={{ maxLength }} />);
        } else if (min && value && value < min) {
          reject(<IntlMessages id="dynamic.form.min.message" values={{ min }} />);
        } else if (min && value && value > max) {
          reject(<IntlMessages id="dynamic.form.max.message" values={{ max }} />);
        } else if (maxCount && value && value.length > maxCount) {
          reject(<IntlMessages id="dynamic.form.maxCount.message" values={{ maxCount }} />);
        }
      }

      resolve();
    });
  };

// PROPS
const getSelectProps = component => ({
  options: component.componentProps.options,
  value: Array.isArray(component?.selectValue)
    ? component.selectValue.filter(item => item !== null)
    : [],
});

const getListSelectProps = component => ({
  optionListId: component.componentProps.optionListId,
});

const getTextProps = component => {
  return {
    autocomplete: component.componentProps.autocomplete ?? false,
    placeholder: component.componentProps.placeholder ?? null,
    validator: myValidator({
      minLength: component.componentProps.minLength,
      maxLength: component.componentProps.maxLength,
    }),
    value: component?.textValue ?? '-',
  };
};

const getNumberProps = component => {
  return {
    validator: myValidator({
      min: component.componentProps.min,
      max: component.componentProps.max,
    }),
    value: component?.numberValue,
  };
};

const getTagProps = component => {
  return {
    validator: myValidator({
      minLength: component.componentProps.minLength || null,
      maxLength: component.componentProps.maxLength || null,
      maxCount: component.componentProps.maxCount || null,
      typeComponent: 'tag',
    }),
    value: Array.isArray(component?.tagValue)
      ? component.tagValue.filter(item => item !== null)
      : [],
  };
};

const getToggleProps = component => {
  return {
    valuePropName: 'checked',
    checked: component.componentProps.checked ?? false,
    value: component?.booleanValue ?? false,
  };
};

const getDateProps = component => {
  const rawValue = component?.dateValue;
  let value = null;

  if (rawValue) {
    if (rawValue.seconds) {
      value = new Date(rawValue.seconds * 1000);
    } else if (rawValue instanceof Date) {
      value = rawValue;
    } else {
      value = new Date(rawValue);
    }
  }

  return {
    includesTime: component.componentProps.includesTime ?? false,
    value,
  };
};

const getPhotoProps = component => ({
  component,
  // An unset (or 0) maxCount means the event type puts no limit on photos.
  maxCount: component.componentProps?.maxCount || undefined,
  maxSize: component.componentProps?.maxSize,
  // The event-type editor stores origin on the component itself, not under componentProps.
  origin: component.origin ?? 'capture',
  value: Array.isArray(component.photoValue) ? component.photoValue : [],
});

const getSignatureProps = component => ({
  component,
  value: component.signatureValue?.url
    ? component.signatureValue
    : component.clarificationValue?.trim() || undefined,
});

const componentProps = {
  select: component => getSelectProps(component),
  listSelect: component => getListSelectProps(component),
  text: component => getTextProps(component),
  number: component => getNumberProps(component),
  tag: component => getTagProps(component),
  toggle: component => getToggleProps(component),
  date: component => getDateProps(component),
  photo: component => getPhotoProps(component),
  signature: component => getSignatureProps(component),
};

export const dynamicFormFactory = form => {
  const { components } = form;
  return components.map(component => ({
    id: component?.id,
    type: component?.type,
    order: component?.order,
    label: component?.label?.name,
    description: component?.label?.description,
    required: component?.commonProps?.required,
    ...(componentProps[component?.type] ? componentProps[component?.type](component) : {}),
  }));
};
